/**
 * "Are these relatives already recorded?" — the predicates behind the Add Relative
 * surfaces.
 *
 * There were two copies of these: one in the sidebar's `AddRelativePanel` and one
 * in the context menu's `AddRelativeMenu`. They were near-identical, and the small
 * difference between them was a defect. The sidebar resolved the person through
 * `ownerPid` (so a proxy node reads the person it stands in for) while the menu
 * read the raw laid-out node — so on a proxy, one surface reported the OWNER's
 * parents and the other the proxy's, and the two disagreed about whether Parents
 * was still offerable. Extracted for TGCLOUD-1644 so both surfaces answer from one
 * implementation, and the proxy-aware read is the only read.
 *
 * Every function takes the person as already resolved by `resolveSelf` — pass the
 * raw node and they will happily answer about a proxy.
 */

/**
 * The member record a relative should actually be added to.
 *
 * A proxy node is a second drawing of somebody already on the chart, so anything
 * structural about it belongs to `proxyOf`. Falls back to the node itself when
 * there is no matching member — the walkthrough anchor-drift guard renders these
 * surfaces with no backend behind them, and a null here would throw before a
 * single anchor reached the DOM.
 */
export const resolveOwnerPid = (node) =>
  node?.isProxy && node?.proxyOf != null ? node.proxyOf : node?.pid;

export const resolveSelf = (node, members) => {
  const ownerPid = resolveOwnerPid(node);
  return (members || []).find((m) => m.pid === ownerPid) || node;
};

/** One parent is enough — a sibling can hang off a single recorded parent. */
export const hasParents = (self) => !!(self?.fatherId || self?.motherId);

/** Both, which is what adding grandparents needs to have something to attach to. */
export const hasBothParents = (self) => !!(self?.fatherId && self?.motherId);

/**
 * True when EITHER parent already has a parent recorded. Deliberately not "all
 * four grandparents": the add creates whichever set is missing, so the tile is
 * spent as soon as one side exists — offering it again would duplicate.
 */
export const hasGrandparents = (self, members) => {
  if (!hasBothParents(self)) return false;
  const list = members || [];
  const father = list.find((p) => p.pid === self.fatherId);
  const mother = list.find((p) => p.pid === self.motherId);
  return !!(
    (father && (father.fatherId || father.motherId)) ||
    (mother && (mother.fatherId || mother.motherId))
  );
};
